import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Typography, Paper, Box, Grid } from "@mui/material";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { v4 as uuid } from "uuid";
import { Formik } from "formik";
import * as Yup from "yup";
import { useStore } from "../../app/stores/store";
import { MemberFormValues } from "../../app/models/member";
import LoadingComponent from "../../app/layout/LoadingComponent";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import CustomTextArea from "../../app/common/form/CustomTextArea";
import CustomDateInput from "../../app/common/form/CustomDateInput";
import TabContainer, { TabData } from "../../app/common/TabContainer";
import MemberPayments from "./MemberPayments";
import MemberFamilyMembers from "./MemberFamilyMembers";
import CustomSelectInput from "../../app/common/form/CustomSelectInput";
import CustomRadioInput from "../../app/common/form/CustomRadioInput";
import { toast } from "react-toastify";
import { useSubscriptionTypeOptions } from "../../app/common/enums/subscriptionType";
import { usePaymentMethodOptions } from "../../app/common/enums/paymentType";
import { useMemberStatusOptions } from "../../app/common/enums/memberStatus";
import { useSalutationTypeOptions } from "../../app/common/enums/salutationType";
import MemberDocuments from "./MemberDocuments";
import CustomCheckbox from "../../app/common/form/CustomCheckBox";
import MembershipPaymentsByYear from "./MembershipPaymentsByYear";
import MemberActivityHistory from "./MemberActivityHistory";
import agent from "../../app/api/agent";
import { Tenant } from "../../app/models/tenant";

export default function MemberForm() {
  const { t } = useTranslation();
  const [member, setMember] = useState<MemberFormValues>(
    new MemberFormValues()
  );
  const memberStore = useStore((s) => s.memberStore);
  const emailLogStore = useStore((s) => s.emailLogStore);
  const { createMember, updateMember, loadMember, loadingInitial } = memberStore;
  const navigate = useNavigate();
  const memberStatusOptions = useMemberStatusOptions();
  const { id } = useParams();
  const commonRequiredMessage = t("members.requiredField");
  const subscriptionTypeOptions = useSubscriptionTypeOptions();
  const paymentMethodOptions = usePaymentMethodOptions();
  const salutationOptions = useSalutationTypeOptions();
  const [letters, setLetters] = useState<any[]>([]);
  const [tenantConfig, setTenantConfig] = useState<Tenant | null>(null);

  const validationSchema = Yup.object({
    firstName: Yup.string().required(
      `${t("members.firstName")} ${commonRequiredMessage}`
    ),
    lastName: Yup.string().required(
      `${t("members.lastName")} ${commonRequiredMessage}`
    ),
    country: Yup.string().required(
      `${t("members.country")} ${commonRequiredMessage}`
    ),
    city: Yup.string().required(
      `${t("members.city")} ${commonRequiredMessage}`
    ),
    zipCode: Yup.string().required(
      `${t("members.zipCode")} ${commonRequiredMessage}`
    ),
  });

  useEffect(() => {
    if (id)
      loadMember(id).then((member: any) =>
        setMember(new MemberFormValues(member))
      );
  }, [id, loadMember]);

  useEffect(() => {
    agent.Tenants.getCurrent().then(setTenantConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (id) {
      emailLogStore.getLettersForMember(id).then(setLetters);
    }
  }, [id, emailLogStore]);

  const minimumAnnualMembershipFee = tenantConfig?.minimumMembershipFee ?? 120;
  const individualAnnualMembershipFee = tenantConfig?.individualMembershipFee ?? 240;
  const familyAnnualMembershipFee = tenantConfig?.familyMembershipFee ?? 360;

  const minimumMonthlyMembershipFee = minimumAnnualMembershipFee / 12;
  const individualMonthlyMembershipFee = individualAnnualMembershipFee / 12;
  const familyMonthlyMembershipFee = familyAnnualMembershipFee / 12;

  const handleFormSubmit = async (member: MemberFormValues) => {
    try {
      if (!member.id) {
        const newMember = { ...member, id: uuid() };
        await createMember(newMember);
        navigate(`/members`);
      } else {
        await updateMember(member);
        toast.success(t("members.successfullyUpdated"));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDownloadRegistrationForm = () => {
    if (!member?.id) return;
    agent.Members.downloadRegistrationFormPdf(member);
  };

  const letterTab: TabData = {
    menuItem: t("emails.lettersHistory.title"),
    render: () => (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("emails.lettersHistory.createdAt")}</TableCell>
              <TableCell>{t("emails.lettersHistory.subject")}</TableCell>
              <TableCell>{t("emails.lettersHistory.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {letters.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {t("dataTable.noData")}
                </TableCell>
              </TableRow>
            ) : (
              letters.map((letter) => (
                <TableRow key={letter.id}>
                  <TableCell>
                    {letter.createdAt &&
                      letter.createdAt !== "0001-01-01T00:00:00" &&
                      letter.createdAt !== "1970-01-01T00:00:00"
                      ? new Date(letter.createdAt).toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell>{letter.subject}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => emailLogStore.downloadLetterPdf(letter.id)}
                    >
                      {t("emails.lettersHistory.downloadLetterPdf")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    ),
  };

  const tabs: TabData[] = [
    {
      menuItem: t("members.membershipPaymentsByYear"),
      render: () => <MembershipPaymentsByYear />,
    },
    { menuItem: t("members.payments"), render: () => <MemberPayments /> },
    {
      menuItem: t("members.familyMembers"),
      render: () => <MemberFamilyMembers />,
    },
    {
      menuItem: t("members.documents"),
      render: () => <MemberDocuments />,
    },
    {
      menuItem: t("members.activityHistory"),
      render: () => <MemberActivityHistory memberId={member.id!} />,
    },
    letterTab
  ];

  if (loadingInitial) return <LoadingComponent content={t("general.loading")} />;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        {member.id ? (
          <Typography variant="h4" component="h2">
            {`${member.memberNumber} - ${member.firstName} ${member.lastName} - ${t("members.editData")}`}
          </Typography>
        ) : null}
        {member.id && (
          <Button
            variant="contained"
            color="success"
            type="button"
            onClick={handleDownloadRegistrationForm}
            size="small"
            sx={{ ml: 2 }}
          >
            {t("members.registrationFormButton")}
          </Button>
        )}
      </Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={member}
          onSubmit={(values) => handleFormSubmit(values)}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("members.basicData")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomRadioInput
                    name="salutation"
                    label={t("members.salutation")}
                    flexDirection={"row"}
                    options={salutationOptions}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="firstName"
                    placeholder={t("members.firstName")}
                    label={t("members.firstName")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="lastName"
                    placeholder={t("members.lastName")}
                    label={t("members.lastName")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="profession"
                    placeholder={t("members.profession")}
                    label={t("members.profession")}
                  />
                </Grid>
                {member.id && (
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <CustomCheckbox
                      name="isRegisteredOnline"
                      label={t("members.isRegisteredOnline")}
                      disabled={true}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={2}>
                {member.id && (
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <CustomTextInput
                      name="memberNumber"
                      placeholder={t("members.memberNumber")}
                      label={t("members.memberNumber")}
                      readOnly={true}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="placeOfBirth"
                    placeholder={t("members.placeOfBirth")}
                    label={t("members.placeOfBirth")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomDateInput
                    name="dateOfBirth"
                    label={t("members.dateOfBirth")}
                    placeholderText={t("members.dateOfBirth")}
                    dateFormat="dd.MM.yyyy"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomDateInput
                    name="joinedAt"
                    label={t("members.joiningDate")}
                    placeholderText={t("members.joiningDate")}
                    dateFormat="dd.MM.yyyy"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomRadioInput
                    name="memberShipFee"
                    label=""
                    options={[
                      {
                        key: String(minimumAnnualMembershipFee),
                        text: t("registrationForm.minimumContribution", { amount: minimumMonthlyMembershipFee }),
                        value: minimumAnnualMembershipFee,
                      },
                      {
                        key: String(individualAnnualMembershipFee),
                        text: t("registrationForm.individualMembership", { amount: individualMonthlyMembershipFee }),
                        value: individualAnnualMembershipFee,
                      },
                      {
                        key: String(familyAnnualMembershipFee),
                        text: t("registrationForm.familyMembership", { amount: familyMonthlyMembershipFee }),
                        value: familyAnnualMembershipFee,
                      },
                    ]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomRadioInput
                    options={subscriptionTypeOptions}
                    name="subscriptionType"
                    label={t("members.subscriptionType")}
                    flexDirection={"column"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomSelectInput
                    options={memberStatusOptions}
                    name="status"
                    placeholder={t("members.status")}
                    label={t("members.status")}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                {t("members.address")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="country"
                    placeholder={t("members.country")}
                    label={t("members.country")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="city"
                    placeholder={t("members.city")}
                    label={t("members.city")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="street"
                    placeholder={t("members.street")}
                    label={t("members.street")}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="zipCode"
                    placeholder={t("members.zipCode")}
                    label={t("members.zipCode")}
                    required
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                {t("members.contactData")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="phone"
                    placeholder={t("members.phone")}
                    label={t("members.phone")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="mobilePhone"
                    placeholder={t("members.mobile")}
                    label={t("members.mobile")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <CustomTextInput
                    name="email"
                    placeholder={t("members.email")}
                    label={t("members.email")}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                {t("members.bankData")}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomRadioInput
                    name="paymentMethod"
                    label={t("members.paymentMethod")}
                    flexDirection={"column"}
                    options={paymentMethodOptions}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="bankName"
                    placeholder={t("members.bankName")}
                    label={t("members.bankName")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="iban"
                    placeholder={t("members.iban")}
                    label={t("members.iban")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextInput
                    name="bic"
                    placeholder={t("members.bic")}
                    label={t("members.bic")}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                {t("members.otherData")}
              </Typography>
              <CustomTextArea
                rows={3}
                name="note"
                placeholder={t("members.note")}
                label={t("members.note")}
              />

              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || !dirty || !isValid}
                type="submit"
                sx={{ float: "right", mt: 2, ml: 2 }}
              >
                {t("general.confirm")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                component={Link}
                to="/members"
                type="button"
                sx={{ float: "right", mt: 2 }}
              >
                {t("general.cancel")}
              </Button>
            </form>
          )}
        </Formik>
      </Paper>

      {id ? (
        <Paper sx={{ p: 2 }}>
          <TabContainer tabs={tabs} />
        </Paper>
      ) : null}
    </>
  );
}
